import {
  initializePermissionApi,
  PermissionApi,
  type CreateBulkProducts201Response,
  type CreateProductDto,
  type ProductEntity,
} from '@data-delivery/permission-ui-api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { assignmentsKeys } from '../../assignments/assignments-keys';
import { businessGroupsKeys } from '../../business-groups/server/business-groups-keys';
import { permissionQueryClient as qc } from '../../query-client';
import { sftpUsersKeys } from '../../sftp-users/server/sftp-users-keys';
import { productsKeys, productsQueries } from './products-keys';

const productsApi = initializePermissionApi(PermissionApi.ProductsApi);

export function useProductsRQ() {
  return useQuery(productsQueries.list());
}

export function useProductByIdRQ(id: string | undefined) {
  const resolvedId = id ?? '';
  return useQuery(productsQueries.byId(resolvedId));
}

export function useBulkCreateProductsMutation() {
  return useMutation<CreateBulkProducts201Response, unknown, CreateProductDto[]>({
    mutationKey: [...productsKeys.all(), 'bulk-create'],
    async mutationFn(products) {
      const { data } = await productsApi.createBulkProducts({ createProductDto: products });
      return data;
    },
    onSuccess() {
      qc.invalidateQueries({ queryKey: productsKeys.list() });
    },
  });
}

export function useUpdateProductMutation(id: string) {
  return useMutation({
    mutationKey: [...productsKeys.all(), 'update', id],
    async mutationFn(vars: Parameters<typeof productsApi.updateProduct>[0]) {
      const { data } = await productsApi.updateProduct(vars);
      return data;
    },
    onSuccess(updated) {
      qc.setQueryData(productsKeys.list(), (old: unknown) => {
        if (!Array.isArray(old)) {
          return old;
        }

        return old.map(p =>
          p && typeof p === 'object' && 'id' in p && (p as { id: string }).id === updated.id
            ? updated
            : p,
        );
      });
      qc.invalidateQueries({ queryKey: productsKeys.byId(updated.id) });
      qc.invalidateQueries({ queryKey: productsKeys.list() });
    },
  });
}

export function useDeleteProductMutation() {
  return useMutation({
    mutationKey: [...productsKeys.all(), 'delete'],
    async mutationFn(vars: Parameters<typeof productsApi.deleteProduct>[0]) {
      const { data } = await productsApi.deleteProduct(vars);
      return data;
    },
    onSuccess(_, vars) {
      const { id } = vars as { id: string };
      qc.setQueryData(productsKeys.list(), (old: unknown) => {
        if (!Array.isArray(old)) {
          return old;
        }

        return old.filter(
          p => !(p && typeof p === 'object' && 'id' in p && (p as { id: string }).id === id),
        );
      });
      qc.invalidateQueries({ queryKey: productsKeys.all() });
      qc.invalidateQueries({ queryKey: assignmentsKeys.all() });
      qc.invalidateQueries({ queryKey: businessGroupsKeys.all() });
      qc.invalidateQueries({ queryKey: sftpUsersKeys.all() });
    },
  });
}

export function useCreateProductMutation() {
  return useMutation<ProductEntity, unknown, CreateProductDto>({
    mutationKey: [...productsKeys.all(), 'create'],
    async mutationFn(vars) {
      const { data } = await productsApi.createProduct({ createProductDto: vars });
      return data;
    },
    onSuccess(created) {
      qc.setQueryData(productsKeys.list(), (old: unknown) => {
        if (!Array.isArray(old)) {
          return old;
        }

        if (
          old.some(
            p => p && typeof p === 'object' && 'id' in p && (p as { id: string }).id === created.id,
          )
        ) {
          return old;
        }

        return [...old, created];
      });
      qc.invalidateQueries({ queryKey: productsKeys.all() });
      qc.invalidateQueries({ queryKey: assignmentsKeys.all() });
      qc.invalidateQueries({ queryKey: businessGroupsKeys.all() });
      qc.invalidateQueries({ queryKey: sftpUsersKeys.all() });
    },
  });
}
